import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DUPLICATE_CHECK_FIELDS } from '../constants/duplicateCheckFields'
import { FieldsType } from '../types/workflow.types'
import {
  Accordion,
  ACTION_ICONS_NAMESPACE,
  CustomButton,
  FormInput,
  MODE_CONSTANTS,
  Modal,
  TableActionType,
  formatPhoneNumber,
  FORMAT_PATTERN,
} from '@ui-kit'
import {
  addToaster,
  API_END_POINT,
  DataTypeStore,
  ENTITY_MANAGER_END_POINTS,
  EntityDedupPayload,
  EntityLayoutJsonType,
  fetchDuplicateRecordsAPI,
  getAllLayoutApi,
  getGenericEntityListingAPI,
  getLayoutByEntityIdAndTypeApi,
  hideLoader,
  LayoutResponseType,
  showLoader,
  transformToDuplicateApiPayload,
} from '@app-store'
import {
  componentNames,
  ENTITY_ID,
  fieldKeyEntityMapping,
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
} from '../utils/constants'
import { ListingTable } from './listing-table/ListingTable'
import { ListingDataResponse, ListingTableColumn } from '../utils/types'
import { APPLICATION_PREFERENCES_STRINGS, DUPLICATION } from '../utils/strings'
import { DuplicationScore } from './DuplicationScore'
import {
  DuplicateResponse,
  Group,
  PotentialMatchTabProps,
} from '../types/types'
import { ListingTableRow } from '../types/listingTable.types'
import { FieldValues } from 'react-hook-form'
import { getNestedValue } from '../utils/helperFunctions'
import { useAppDispatch, useAppSelector } from '../utils/agent.store'
import FormRenderer from './FormRenderer'
import {
  EntityLayout,
  EntityLayoutResponse,
  LayoutData,
} from '../types/layout.types'
import { isEmpty, isEqual, noop } from 'lodash'
import { addEntityData, getEntityData } from '../apis/entityData.api'
import { isArray } from 'lodash'
import { TOASTER_NAMESPACE } from '../constants/addEditPage.strings'
import { v4 as uuid } from 'uuid'
import AddEditRecordPage from '../pages/AddEditViewRecord.page'
import useCustomFetchAgent from '../hooks/useCustomFetchAgent'
import moment from 'moment'

const getFormInputValue = ({
  field,
  rawValue,
  isStateField,
  mappedStateName,
  isPhoneField,
}: {
  field: FieldsType
  rawValue: string
  isStateField: boolean
  mappedStateName?: string
  isPhoneField: boolean
}) => {
  if (field.type === componentNames.DATE) {
    return rawValue ? moment(rawValue).format('MM-DD-YYYY') : ''
  }

  if (isStateField && mappedStateName) {
    return mappedStateName
  }

  if (isPhoneField && rawValue) {
    return formatPhoneNumber(rawValue, FORMAT_PATTERN.PHONE_INPUT)
  }

  return rawValue ?? ''
}

const PotentialMatchTab: React.FC<PotentialMatchTabProps> = ({
  entityName = 'PATIENT',
  apiData,
  closePotentialMatchTab,
  fetchAndSetLookupValue,
}) => {
  const [selectedRow, setSelectedRow] = useState<ListingTableRow>()
  const [listingLayout, setListingLayout] = useState<EntityLayoutJsonType>()
  const [dedupePayload, setDedupePayload] = useState<EntityDedupPayload>()
  const [duplicateRecords, setDuplicateRecords] = useState<ListingTableRow[]>(
    []
  )
  const lastFetchRecordRef = useRef<{
    res: DuplicateResponse
    payload: EntityDedupPayload
  }>()
  const [viewLayout, setViewLayout] = useState<LayoutResponseType>()
  const [viewDuplicateRecord, setViewDuplicateRecord] = useState<boolean>(false)
  const [viewRecordData, setViewRecordData] = useState<FieldValues>()

  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [viewModal, setViewModal] = useState<{
    state: boolean
    recordId: number
  }>({ state: false, recordId: 0 })

  const [recordId, setRecordId] = useState<number>(0)
  const [mappedStateName, setMappedStateName] = useState<string>('')

  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )

  const { data: allLayouts } = useCustomFetchAgent<EntityLayoutResponse>(
    `${API_END_POINT.V3_ENTITY_MANAGER}${ENTITY_MANAGER_END_POINTS.LAYOUT}`,
    {
      params: {
        type: NAMESPACES.EDIT_MODE,
        entityId: ENTITY_ID[entityName],
        defaultValue: true,
      },
    }
  )
  const dispatch = useAppDispatch()

  const data = useMemo(() => {
    let layouts = isArray(allLayouts) ? allLayouts : allLayouts?.content
    layouts = layouts
      ?.filter((layout) => !layout.deleted)
      .sort((a, b) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      })
    layouts = layouts?.filter(
      (layout) =>
        layout.default_value === true &&
        layout.type === LAYOUT_TYPE_NAMESPACE.DEFAULT_VIEW_EDIT
    )
    return layouts
  }, [allLayouts])

  const extraColumns: ListingTableColumn[] = [
    {
      header: '',
      accessor: DUPLICATION.SELECT_ACCESSOR,
      type: DUPLICATION.RADIO_TYPE,
      onClick(_KeyName, row, _rowIndex) {
        setSelectedRow(row)
      },
    },
    {
      header: APPLICATION_PREFERENCES_STRINGS.MATCH_SCORE,
      accessor: DUPLICATION.SCORE_ACCESSOR,
      type: DUPLICATION.COMPONENT_TYPE,
      renderComponent: (row) => {
        return (
          <div className="flex items-center gap-1">
            <span>{String(row?.score ?? '')}</span>
            <DuplicationScore
              groups={row?.matchDetails as unknown as Group[]}
            />
          </div>
        )
      },
    },
  ]
  const extraActions: (TableActionType<DataTypeStore> & {
    onClick?: (row: ListingTableRow, rowIndex: number) => void
  })[] = [
    {
      label: NAMESPACES.VIEW,
      key: ACTION_ICONS_NAMESPACE.VIEW,
      onClick: async (row) => {
        const layoutId = await fetchViewLayout()
        setViewDuplicateRecord(true)
        fetchViewRecord(layoutId, row?.id)
      },
    },
  ]

  const fetchViewRecord = async (
    layoutId: number,
    recordId?: string | number
  ) => {
    try {
      dispatch(showLoader())
      const res = await getEntityData(
        listingLayout?.entity?.entity_name ?? '',
        Number(ENTITY_ID[entityName]?.toString() ?? ''),
        Number(layoutId),
        recordId ?? ''
      )
      if (res?.data) {
        setViewRecordData(res.data)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchViewLayout = async () => {
    dispatch(showLoader())
    try {
      const res = await getLayoutByEntityIdAndTypeApi({
        entityId: ENTITY_ID[entityName]?.toString() ?? '',
        type: LAYOUT_TYPE_NAMESPACE?.OVERVIEW_TYPE,
        isDefault: true,
      })
      if (res?.data) {
        setViewLayout(res.data?.[0])
      }
      return res?.data?.[0]?.id
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const getStateIdFromApiData = (): number | null => {
    if (!apiData) return null

    return (
      apiData?.c_patient_addresses_c_patients?.[0]?.state_id ||
      apiData?.state_id ||
      null
    )
  }

  const fetchStateMappingByID = async (id: number) => {
    document.body.classList.add('cursor-wait')
    try {
      const res = await getGenericEntityListingAPI({
        entity_id: ENTITY_ID.PATIENT_ADDRESS ?? '',
        select: ['name'],
        filters: [
          {
            criteria: [
              {
                field_name: 'id',
                operation: 'EQUALS',
                values: [String(id)],
              },
            ],
          },
        ],
      })
      return res?.[0]?.name ?? ''
    } catch (err) {
      console.error(err)
    } finally {
      document.body.classList.remove('cursor-wait')
    }
  }

  const addSelectedFlag = (data: ListingTableRow[]): ListingTableRow[] =>
    data?.map((row: ListingTableRow) => ({
      ...row,
      select: row?.id === selectedRow?.id ? true : false,
    }))

  const dataFetchCallback = async (
    entityId: number,
    payload?: EntityDedupPayload
  ): Promise<ListingDataResponse> => {
    if (!payload) {
      return {
        content: [],
        totalPages: 0,
      }
    }

    let duplicateRes = lastFetchRecordRef.current?.res
    const prevPayload = lastFetchRecordRef.current?.payload

    if (!prevPayload || !isEqual(payload, prevPayload)) {
      duplicateRes = await fetchDuplicateRecordsAPI(entityId, payload, 0, 50)
      lastFetchRecordRef.current = { res: duplicateRes, payload }
    } else {
      duplicateRes = lastFetchRecordRef.current?.res
    }

    const rawContent = duplicateRes?.data?.content ?? []
    const content = addSelectedFlag(rawContent)

    setDuplicateRecords(content)

    return {
      content,
      totalPages: (duplicateRes?.data?.totalPages as number) ?? 0,
    }
  }

  const createRecord = async (values?: FieldValues) => {
    dispatch(showLoader())
    try {
      const updatedApiData =
        workflowDefinition?.global_variables?.multiIntake &&
        apiData?.c_patient_addresses_c_patients?.[0]
          ? {
              ...apiData,
              c_patient_addresses_c_patients:
                apiData.c_patient_addresses_c_patients.map(
                  (
                    item: (typeof apiData.c_patient_addresses_c_patients)[number],
                    index: number
                  ) => (index === 0 ? { ...item, is_preferred: true } : item)
                ),
            }
          : apiData
      const res = await addEntityData(
        String(listingLayout?.entity?.entity_name),
        Number(listingLayout?.entity?.entity_id),
        Number(data?.[0]?.id),
        updatedApiData ?? (values as FieldValues)
      )
      dispatch(
        addToaster({
          message: TOASTER_NAMESPACE.ADD_SUCCESS,
          type: 'success',
          id: uuid(),
        })
      )
      setViewModal({ state: true, recordId: res?.data?.id })
      setRecordId(res?.data?.id)
    } catch (err) {
      dispatch(
        addToaster({
          message: TOASTER_NAMESPACE.FAILED_TO_ADD_RECORD,
          type: 'error',
          id: uuid(),
        })
      )
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const attachRecordToCase = async (recordId: number) => {
    await fetchAndSetLookupValue?.(recordId)
    closePotentialMatchTab && closePotentialMatchTab()
  }

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        let listingLayoutId, editLayout
        const apiDataDup = JSON.parse(JSON.stringify(apiData))
        const entityId = ENTITY_ID[entityName]?.toString()
        if (!entityId || !apiData) return
        const allLayouts = await getAllLayoutApi({
          entityId: entityId,
          defaultValue: true,
        })
        for (const layout of allLayouts?.data?.content ?? []) {
          if (layout.type === LAYOUT_TYPE_NAMESPACE.LAYOUT_LISTING) {
            listingLayoutId = String(layout.id ?? 0)
            setListingLayout(layout as EntityLayoutJsonType)
          } else if (layout.type === LAYOUT_TYPE_NAMESPACE.DEFAULT_VIEW_EDIT) {
            editLayout = layout
          }
        }
        if (Object.prototype.hasOwnProperty.call(apiData, 'facility_type'))
          delete apiDataDup['facility_type']

        apiDataDup['group_npi'] = apiDataDup['npi']

        const payload = transformToDuplicateApiPayload(
          apiDataDup,
          entityId,
          listingLayoutId ?? '',
          editLayout
        )
        dataFetchCallback(+entityId, payload)

        setDedupePayload(payload)
      } catch (error) {
        console.error('Failed to fetch initial data', error)
      }
    }

    fetchInitialData()
  }, [entityName])

  useEffect(() => {
    const fetchStateName = async () => {
      const stateId = getStateIdFromApiData()
      if (!stateId) return

      const name = await fetchStateMappingByID(Number(stateId))
      setMappedStateName(name)
    }

    fetchStateName()
  }, [apiData, entityName])

  return (
    <>
      <Accordion heading="Duplicate Check Fields" isDefaultActive>
        <div className="grid grid-cols-4 gap-4 p-4">
          {DUPLICATE_CHECK_FIELDS[entityName]?.map((field) => {
            const isStateField = field.name === NAMESPACES.STATE
            const isPhoneField = field.isPhone === true
            const rawValue = getNestedValue(apiData, field.key) as string
            return (
              <FormInput
                key={field.key}
                className="col-3"
                label={field.label}
                name={field.name}
                value={getFormInputValue({
                  field,
                  rawValue,
                  isStateField,
                  mappedStateName,
                  isPhoneField,
                })}
              />
            )
          })}
        </div>
      </Accordion>
      <div className="flex justify-between my-3">
        <div className="flex flex-col">
          <div className="flex gap-3 items-center">
            <span
              className="font-medium text-xl font-primary cursor-pointer"
              onClick={() => {
                setViewDuplicateRecord(false)
              }}
            >
              {APPLICATION_PREFERENCES_STRINGS?.MATCH_RESULT}{' '}
            </span>
            {viewDuplicateRecord ? (
              <span className="font-medium text-xl font-primary text-primary-450">
                / {NAMESPACES.VIEW} {viewLayout?.entity?.singular_label}
              </span>
            ) : (
              <span>
                {duplicateRecords?.length} {NAMESPACES.RESULTS_FOUND}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {entityName === fieldKeyEntityMapping.patient && (
            <CustomButton isFilled onClick={createRecord}>
              {APPLICATION_PREFERENCES_STRINGS?.CREATE_ANYWAY}
            </CustomButton>
          )}
          <CustomButton
            isFilled
            onClick={() => {
              attachRecordToCase(selectedRow?.id ?? 0)
            }}
            disabled={isEmpty(selectedRow)}
          >
            {APPLICATION_PREFERENCES_STRINGS?.USE_EXISTING}
          </CustomButton>
        </div>
      </div>

      {listingLayout !== undefined &&
        ((!viewDuplicateRecord && (
          <ListingTable
            entityId={ENTITY_ID[entityName]}
            entityName={listingLayout?.entity_name}
            layout={listingLayout}
            extraColumns={extraColumns}
            extraActions={extraActions}
            hideFilters={true}
            dataFetchCallback={() =>
              dataFetchCallback(listingLayout.entity_id, dedupePayload)
            }
            refetchKey={selectedRow?.id || -1}
          />
        )) || (
          <FormRenderer
            heading={null}
            layout={viewLayout?.layout_data as LayoutData}
            mode={MODE_CONSTANTS.VIEW}
            handleFormSubmit={noop}
            fullLayout={viewLayout as EntityLayout}
            data={viewRecordData}
          />
        ))}

      {viewModal?.state && (
        <Modal
          isOpen={viewModal?.state}
          width="w-[80%]"
          closeModalOnOutsideClick={false}
          closeModal={() => setViewModal({ state: false, recordId: 0 })}
          showCloseButton={false}
        >
          <AddEditRecordPage
            mode={mode}
            id={viewModal?.recordId?.toString()}
            entityId={data?.[0]?.entity_id?.toString()}
            isEdit={false}
            isHideAuditButton={true}
            allowNavigate={false}
            isFromIntake={true}
            onCancel={async () => {
              await fetchAndSetLookupValue?.(recordId)
              closePotentialMatchTab?.()
              setViewModal({ state: false, recordId: 0 })
            }}
            onEditClickInViewLayout={() => {
              setMode('edit')
            }}
            onSaveClick={async () => {
              await fetchAndSetLookupValue?.(recordId)
              closePotentialMatchTab?.()
              setViewModal({ state: false, recordId: 0 })
            }}
          />
        </Modal>
      )}
    </>
  )
}

export default PotentialMatchTab
